import type { RefObject } from "react";
import { Icon } from "../landing/icons";

/* The three overlays of the Lite page. Fields, ids and behaviour are unchanged; only the markup classes are new. */

export interface UserDetails {
  name: string;
  company: string;
  companyUrl: string;
  email: string;
}

export interface CompanySuggestion {
  name: string;
  domain?: string;
}

export interface ConnectForm {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

const Req = () => (
  <span className="rl-req" aria-hidden="true">
    *
  </span>
);

interface UserDetailsModalProps {
  userDetails: UserDetails;
  companySuggestions: CompanySuggestion[];
  showCompanySuggestions: boolean;
  isLoadingSuggestions: boolean;
  isLoadingReport: boolean;
  companyInputRef: RefObject<HTMLDivElement>;
  suggestionsRef: RefObject<HTMLDivElement>;
  onChange: (field: string, value: string) => void;
  onCompanyFocus: () => void;
  onCompanySelect: (company: CompanySuggestion) => void;
  onClose: () => void;
  onProceed: () => void;
}

export function UserDetailsModal({
  userDetails,
  companySuggestions,
  showCompanySuggestions,
  isLoadingSuggestions,
  isLoadingReport,
  companyInputRef,
  suggestionsRef,
  onChange,
  onCompanyFocus,
  onCompanySelect,
  onClose,
  onProceed,
}: UserDetailsModalProps) {
  return (
    <div className="rl-modal-overlay" onClick={onClose}>
      <div
        className="rl-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rl-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rl-modal__head">
          <div>
            <span className="rl-eyebrow">Almost there</span>
            <h2 className="rl-modal__title" id="rl-details-title">
              Please provide the following details to proceed
            </h2>
          </div>
          <button type="button" className="rl-modal__close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className="rl-modal__body">
          <div className="rl-fields">
            <div className="rl-field">
              <label htmlFor="name">
                Name <Req />
              </label>
              <input
                type="text"
                id="name"
                placeholder="Name *"
                value={userDetails.name}
                onChange={(e) => onChange("name", e.target.value)}
              />
            </div>

            <div className="rl-field">
              <label htmlFor="company">
                Company <Req />
              </label>
              <div className="rl-field__rel" ref={companyInputRef}>
                <input
                  type="text"
                  id="company"
                  placeholder="Company *"
                  value={userDetails.company}
                  onChange={(e) => onChange("company", e.target.value)}
                  onFocus={onCompanyFocus}
                  autoComplete="off"
                />
                {isLoadingSuggestions && <span className="rl-field__loading">Loading...</span>}
                {showCompanySuggestions && companySuggestions.length > 0 && (
                  <div className="rl-suggest" ref={suggestionsRef}>
                    {companySuggestions.map((company, index) => (
                      <div
                        key={index}
                        className="rl-suggest__item"
                        onClick={() => onCompanySelect(company)}
                      >
                        <b>{company.name}</b>
                        {company.domain && <small>{company.domain}</small>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rl-field">
              <label htmlFor="companyUrl">Company URL</label>
              <input
                type="url"
                id="companyUrl"
                placeholder="Company URL"
                value={userDetails.companyUrl}
                onChange={(e) => onChange("companyUrl", e.target.value)}
              />
            </div>

            <div className="rl-field">
              <label htmlFor="email">
                Email <Req />
              </label>
              <input
                type="email"
                id="email"
                placeholder="Email *"
                value={userDetails.email}
                onChange={(e) => onChange("email", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="rl-modal__foot">
          <button type="button" className="rl-btn rl-btn--primary" onClick={onProceed} disabled={isLoadingReport}>
            {isLoadingReport && <span className="rl-spin-dot" aria-hidden="true" />}
            {isLoadingReport ? "Generating Report..." : "Proceed"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConnectModalProps {
  form: ConnectForm;
  onChange: (field: string, value: string) => void;
  onClose: () => void;
  onSend: () => void;
}

export function ConnectModal({ form, onChange, onClose, onSend }: ConnectModalProps) {
  return (
    <div className="rl-modal-overlay" onClick={onClose}>
      <div
        className="rl-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rl-connect-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rl-modal__head">
          <div>
            <h2 className="rl-modal__title" id="rl-connect-title">
              <span className="rl-hl">Connect</span> with our experts
            </h2>
            <p className="rl-modal__sub">Tell us more about your project and how we can help</p>
          </div>
          <button type="button" className="rl-modal__close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className="rl-modal__body">
          <div className="rl-fields">
            <div className="rl-field">
              <label htmlFor="connect-first-name">First Name *</label>
              <input
                type="text"
                id="connect-first-name"
                placeholder="John"
                value={form.firstName}
                onChange={(e) => onChange("firstName", e.target.value)}
              />
            </div>
            <div className="rl-field">
              <label htmlFor="connect-last-name">Last Name *</label>
              <input
                type="text"
                id="connect-last-name"
                placeholder="Doe"
                value={form.lastName}
                onChange={(e) => onChange("lastName", e.target.value)}
              />
            </div>
            <div className="rl-field">
              <label htmlFor="connect-email">Email *</label>
              <input
                type="email"
                id="connect-email"
                placeholder="john@example.com"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
              />
            </div>
            <div className="rl-field">
              <label htmlFor="connect-company">Company *</label>
              <input
                type="text"
                id="connect-company"
                placeholder="Your Company"
                value={form.company}
                onChange={(e) => onChange("company", e.target.value)}
              />
            </div>
            <div className="rl-field rl-field--full">
              <label htmlFor="connect-phone">Phone Number</label>
              <input
                type="tel"
                id="connect-phone"
                placeholder="+1 (555) 000-0000"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
              />
            </div>
            <div className="rl-field rl-field--full">
              <label htmlFor="connect-message">Message *</label>
              <textarea
                id="connect-message"
                placeholder="Tell us about your project and how we can help..."
                rows={4}
                value={form.message}
                onChange={(e) => onChange("message", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="rl-modal__foot">
          <button type="button" className="rl-btn rl-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="rl-btn rl-btn--primary" onClick={onSend}>
            Send Message →
          </button>
        </div>
      </div>
    </div>
  );
}

export function LoadingOverlay() {
  return (
    <div className="rl-loading" role="status" aria-live="polite">
      <div className="rl-loading__in">
        <span className="rl-loading__spinner" aria-hidden="true" />
        <h3>Generating your data+AI roadmap</h3>
        <p>Please wait while we analyze your responses and create a personalized data+AI roadmap...</p>
      </div>
    </div>
  );
}
